import React, { useState, useEffect } from "react";
import { connect, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { Edit } from "@mui/icons-material";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import Button from "../../Button/Button";
import InputController from "../../Input/InputField/InputController";
import email from '../../../../assets/svgs/email.svg';
import { updateEmailStyles as styles } from "./optInForUpdateEmailStyles.tailwind";
import { otpValidationSchema, updateEmailValidationSchema } from "./optInForUpdateEmailValidationSchema";
import { yupResolver } from "@hookform/resolvers/yup";
import { RESEND_OTP_DURATION } from "../../../../lib/constants/globalConstant";
import { optInForUpdates, resendUpdateVerificationCode } from "../../../../redux/slices/rewardSlice";
import { getUserDetails } from "../../../../redux/slices/userSlice";

const OptInForUpdateEmailModal = ({ isOpen, onClose, optIn, resendOTP, updateFieldFromModal, getUser }) => {
  const { user } = useSelector((state) => state.user);
  const { isResendingVerificationCode, isOptingInForUpdates } = useSelector((state) => state.reward);
  const profileData = user?.userDetails;
  const resendOtpDuration = RESEND_OTP_DURATION;

  const [previousValue, setPreviousValue] = useState(null);
  const [otpReceived, setOtpReceived] = useState(false);
  const [countdown, setCountdown] = useState(resendOtpDuration);
  const [timerActive, setTimerActive] = useState(false);
  const [isEditable, setIsEditable] = useState(true);

  const validationSchema = otpReceived ? otpValidationSchema : updateEmailValidationSchema;

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    getValues,
    watch,
    formState: { errors },
  } = useForm({ resolver: yupResolver(validationSchema),  context: { previousValue } });
  const otpValue = watch("otp", "");

  const handleResendOTP = async () => {
    const email = getValues("optInForUpdateEmail");

    const response = await resendOTP({ email });
    if (resendUpdateVerificationCode.fulfilled.match(response)) {
      setOtpReceived(true);
      setCountdown(resendOtpDuration);
      setTimerActive(true);
    }
  };

  const onSubmit = async () => {
    const email = getValues("optInForUpdateEmail");
    const otp = otpReceived ? getValues("otp") : "";

    const response = await optIn({ email, otp });

    if (optInForUpdates.fulfilled.match(response)) {
      const { status } = response.payload;

      if (status === 1) {
        updateFieldFromModal("optInForUpdateEmail", email);
        updateFieldFromModal("emailAlertPreference", 1);
        getUser()
        onClose();
      } else if (status === 2) {
        setOtpReceived(true);
        setCountdown(resendOtpDuration);
        setTimerActive(true);
        setIsEditable(false);
      }
    }
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 6);
    setValue("otp", value);
  };

  const handleEditClick = () => {
    setIsEditable(true);
    setOtpReceived(false);
    setValue("otp", "");
  };

   // Pre-fill previous value
   useEffect(() => {
    if (profileData?.optInForUpdateEmail) {
      setPreviousValue(profileData?.optInForUpdateEmail);
    }else{
      setValue("optInForUpdateEmail", profileData?.userEmail);
    }
  }, [profileData, setValue]);

  // Timer for resend OTP
  useEffect(() => {
    if (otpReceived && countdown > 0) {
      const timer = setInterval(() => setCountdown((prev) => prev - 1), 1000);
      return () => clearInterval(timer);
    }
    if (countdown === 0) {
      setTimerActive(false);
    }
  }, [otpReceived, countdown]);

  useEffect(() => {
    if (!isOpen) {
      setOtpReceived(false);
      setCountdown(resendOtpDuration);
      setTimerActive(false);
      setIsEditable(true);
      reset(); // Reset all form values
    }
  }, [isOpen, reset]);

  return (
    <Modal
      open={isOpen}
      onClose={() => {}}
      sx={{
        padding: "16px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div className={styles.container}>
        <button onClick={onClose} className={styles.closeButton}>
          <CloseIcon sx={{ fontSize: "16px" }} />
        </button>

        <div className={styles.img_heading}>
          <img src={email} alt="email" className={styles.popup_img}></img>
          <div className={styles.heading}>
            {previousValue ? "Update " : "Add "}Opt-in for Updates Email
          </div>
        </div>
       
        {previousValue && (
          <InputController
            control={control}
            showLabel
            name="previousValue"
            label="Previous Email"
            value={previousValue}
            disabled
          />
        )}

        <form className={styles.formContainer} onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.newValueContainer}>
            {otpReceived && (
              <button
                type="button"
                className="absolute text-[12px] right-1 flex items-center text-primaryHeading hover:underline cursor-pointer z-10 -top-1"
                onClick={handleEditClick}
              >
                <Edit fontSize="10" />
                Edit Email
              </button>
            )}

            <InputController
              control={control}
              showLabel
              name="optInForUpdateEmail"
              label={previousValue ? "New Email Address" : "Email Address"}
              placeholder="Enter Email Address"
              error={!!errors.optInForUpdateEmail}
              helperText={errors?.optInForUpdateEmail?.message}
              disabled={!isEditable}
            />

            {otpReceived && (
              <div className={styles.afterOtpreceivedContainer}>
                <div className={styles.spamNote}>
                  OTP sent. Check your inbox or spam folder.
                </div>
                {timerActive ? (
                  <div className={styles.resendOtp}>Resend OTP in {countdown}s</div>
                ) : (
                  <button
                    type="button"
                    className={styles.resendOtp}
                    disabled={isResendingVerificationCode}
                    onClick={handleResendOTP}
                  >
                    {isResendingVerificationCode ? "Resending OTP..." : "Resend OTP"}
                  </button>
                )}
              </div>
            )}
          </div>

          {otpReceived && (
            <div className={styles.otpContainer}>
              <InputController
                control={control}
                showLabel
                name="otp"
                label="Enter OTP"
                type="number"
                onChange={handleOtpChange}
                error={!!errors.otp}
                helperText={errors?.otp?.message}
              />
            </div>
          )}

          <Button
            variant="primary"
            className={styles.button}
            disabled={
              isOptingInForUpdates || (otpReceived && otpValue.length !== 6) // Disable if OTP is not exactly 6 digits
            }
            onClick={handleSubmit(onSubmit)}
          >
            {isOptingInForUpdates ? "Signing up..." : "Sign up"}
          </Button>
        </form>
      </div>
    </Modal>
  );
};

const mapDispatchToProps = (dispatch) => ({
  optIn: (data) => dispatch(optInForUpdates(data)),
  resendOTP: (data) => dispatch(resendUpdateVerificationCode(data)),
  getUser: () => dispatch(getUserDetails()),
});

export default connect(null, mapDispatchToProps)(OptInForUpdateEmailModal);
